/**
 * Turns dense backend prose into readable blocks by:
 * 1) Detecting common list markers (a) b) c), 1) 2), A) B) …)
 * 2) Using Intl.Segmenter (sentence granularity) where available for punctuation-aware breaks
 */

const LONG_LINE = 220

function getSentenceSegmenter(): Intl.Segmenter | null {
  try {
    if (typeof Intl === 'undefined' || !('Segmenter' in Intl)) return null
    return new Intl.Segmenter(undefined, { granularity: 'sentence' })
  } catch {
    return null
  }
}

function splitSentencesFallback(text: string): string[] {
  const parts = text.split(
    /(?<=[.!?…])\s+(?=[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜÇŒÆ"'([])/u
  )
  return parts.map((s) => s.trim()).filter(Boolean)
}

function splitIntoSentences(text: string, segmenter: Intl.Segmenter | null): string[] {
  const t = text.trim()
  if (!t) return []
  if (segmenter) {
    const out = Array.from(segmenter.segment(t), (seg) => seg.segment.trim()).filter(
      Boolean
    ) as string[]
    return out.length ? out : [t]
  }
  return splitSentencesFallback(t)
}

/**
 * Insert line breaks before list continuations (typical ISO / policy text).
 */
function breakBeforeListMarkers(text: string): string {
  let t = text.replace(/\r\n/g, '\n').replace(/\u00a0/g, ' ')

  // Comma / semicolon then next lettered item: "…, b) …"
  t = t.replace(/([,;])\s+([a-z])\)\s+/gi, '$1\n$2) ')
  // New sub-point after b) … z) (avoids breaking the first "a)" in isolation)
  t = t.replace(/\s+([b-z])\)\s+/gi, '\n$1) ')
  // Colon often introduces "… the following: a) …"
  t = t.replace(/([:;])\s*([a-z])\)\s+/gi, '$1\n$2) ')
  // Numbered lists 1) 2) … (short numbers only — avoids ISO 27001-style ids)
  t = t.replace(/\s+(\d{1,3})\)\s+/g, '\n$1) ')
  // Letter lists A) B) … (B–Z; first A) often follows ":" or a line break)
  t = t.replace(/\s+([B-Z])\)\s+/g, '\n$1) ')
  t = t.replace(/([:;])\s*(A)\)\s+/g, '$1\n$2) ')

  return t
}

function breakLongRunsBySentences(
  text: string,
  segmenter: Intl.Segmenter | null
): string {
  const lines = text.split('\n')
  const out: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) {
      out.push('')
      continue
    }
    if (trimmed.length <= LONG_LINE) {
      out.push(trimmed)
      continue
    }
    const sentences = splitIntoSentences(trimmed, segmenter)
    if (sentences.length > 1) {
      out.push(sentences.join('\n\n'))
    } else {
      out.push(trimmed)
    }
  }

  return out.join('\n')
}

export function formatStructuredDescription(
  raw: string | undefined | null
): string {
  if (raw == null || typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''

  const segmenter = getSentenceSegmenter()

  let t = trimmed.replace(/\s+/g, ' ')
  t = breakBeforeListMarkers(t)
  t = breakLongRunsBySentences(t, segmenter)
  return t.replace(/\n{3,}/g, '\n\n').trim()
}
